/**
 * IECN DYNAMIC PRICING ENGINE
 *
 * Pure, deterministic, and the single source of truth for every price in the system.
 * Nothing else in the codebase is allowed to compute a customer charge, a driver payout
 * or a margin. UI components receive numbers — they never derive them.
 *
 * The default rule set below is IECN's PUBLISHED rate card
 * (Docs/06-04-2026-IECN-Pricing-Schedule.pdf). Values marked ASSUMPTION are NOT on the
 * published card; they default to a no-op (0) or are flagged so they cannot silently
 * charge a customer or underpay a driver before IECN sets them.
 *
 * See docs/PRICING_ENGINE.md.
 */
import { addCents, assertCents, clampMin, marginPercent, multiplyCents, percentOf, } from './money.js';
// ---------------------------------------------------------------------------
// Domain vocabulary
// ---------------------------------------------------------------------------
/** Published service classes. Admin-configurable; these are the launch set. */
export const SERVICE_LEVELS = ['ROUTINE', 'RUSH', 'STAT'];
/**
 * Pricing zones.
 *
 * CITY is GEOGRAPHIC, not a distance band: a run is priced at the in-city rate when both
 * the pickup and the dropoff fall inside a service area tagged as city limits. Every other
 * zone is resolved from road distance against the published rate matrix.
 */
export const PRICING_ZONES = ['CITY', 'LOCAL', 'REGIONAL', 'EXTENDED', 'LONG_DISTANCE'];
export const VEHICLE_CLASSES = [
    'CAR',
    'SUV',
    'PICKUP_TRUCK',
    'CARGO_VAN',
    'SPRINTER_VAN',
    'BOX_TRUCK',
];
/** What's in the package. Lives here (not schemas.ts) because it now drives pricing. */
export const PACKAGE_CATEGORIES = [
    'DOCUMENTS',
    'SMALL_PARCEL',
    'MEDICAL_SPECIMEN',
    'MEDICAL_SUPPLIES',
    'PHARMACY',
    'LEGAL_FILING',
    'AUTO_PARTS',
    'BUSINESS_MATERIALS',
    'RETAIL_GOODS',
    'ELECTRONICS',
    'EQUIPMENT',
    'FOOD_CATERING',
    'OTHER',
];
/**
 * Pricing segments — the customer-facing discount lives on this axis, not on service level
 * or zone. MEDICAL keeps the published rate card unchanged; STANDARD gets the discounted
 * non-sensitive rates set in Settings.
 */
export const PRICING_SEGMENTS = ['STANDARD', 'MEDICAL'];
/** Categories treated as sensitive enough to always bill at full (MEDICAL) pricing. */
export const MEDICAL_PACKAGE_CATEGORIES = [
    'MEDICAL_SPECIMEN',
    'MEDICAL_SUPPLIES',
    'PHARMACY',
];
/**
 * A delivery carrying even one sensitive item bills the whole run at full price — a
 * cheaper non-medical rate should never end up covering a specimen or a pharmacy order
 * just because it shared a run with a document.
 */
export function resolvePricingSegment(items) {
    const hasSensitiveItem = (items ?? []).some((item) => MEDICAL_PACKAGE_CATEGORIES.includes(item.category));
    return hasSensitiveItem ? 'MEDICAL' : 'STANDARD';
}
export const SERVICE_LEVEL_SPECS = {
    ROUTINE: {
        key: 'ROUTINE',
        label: 'Routine',
        promise: 'Same-Day · 4 hr',
        description: 'Same-day within a 4-hour window. Scheduled, non-urgent runs.',
        deliverWithinMinutes: 240,
        pickupTargetMinutes: 120,
    },
    RUSH: {
        key: 'RUSH',
        label: 'Rush',
        promise: 'Express · 2–4 hr',
        description: 'Expedited 2–4 hour delivery for time-sensitive items.',
        deliverWithinMinutes: 240,
        pickupTargetMinutes: 60,
    },
    STAT: {
        key: 'STAT',
        label: 'STAT',
        promise: 'Priority · under 2 hr',
        description: 'Priority under 2 hours — critical specimens, meds and documents.',
        deliverWithinMinutes: 120,
        pickupTargetMinutes: 30,
    },
};
export const ZONE_SPECS = {
    CITY: {
        key: 'CITY',
        label: 'In-City',
        fromMiles: 0,
        toMiles: null,
        bandLabel: 'Within city limits',
    },
    /**
     * Boundaries mirror the per-mile schedule's bands (LOCAL 0-20, REGIONAL 21-51, then one
     * 52+ band) so the two pricing cards a customer compares describe the same distances.
     * EXTENDED and LONG_DISTANCE already carry identical dollar rates, so widening REGIONAL
     * only pushes that existing split further out — it does not change what either charges.
     */
    LOCAL: { key: 'LOCAL', label: 'Local', fromMiles: 0, toMiles: 20, bandLabel: '0–20 mi' },
    REGIONAL: { key: 'REGIONAL', label: 'Regional', fromMiles: 20, toMiles: 51, bandLabel: '21–51 mi' },
    EXTENDED: { key: 'EXTENDED', label: 'Extended', fromMiles: 51, toMiles: 72, bandLabel: '52–72 mi' },
    LONG_DISTANCE: {
        key: 'LONG_DISTANCE',
        label: 'Long-Distance',
        fromMiles: 72,
        toMiles: null,
        bandLabel: '73+ mi',
    },
};
// ---------------------------------------------------------------------------
// Pricing modes — the customer chooses
// ---------------------------------------------------------------------------
/**
 * IECN quotes a job both ways and lets the customer pick.
 *
 *   FLAT_RATE — the published zone matrix. One price per band; mileage only beyond 40 mi.
 *   PER_MILE  — the published per-mile schedule, tiered by distance band.
 *
 * They cross over at roughly 6–7 miles: per-mile is cheaper on short hops, the flat card is
 * far cheaper on long hauls (a 50-mile routine run is $82.50 flat against $225 per-mile).
 * Offering both therefore means IECN receives the LOWER of the two on any job where the
 * customer optimises. That is a deliberate competitive choice — see docs/PRICING_ENGINE.md.
 */
export const PRICING_MODES = ['FLAT_RATE', 'PER_MILE'];
/** Distance bands on the per-mile schedule. These are NOT the flat card's zone bands. */
export const MILEAGE_BANDS = ['LOCAL', 'REGIONAL', 'MID_RANGE', 'LONG_DISTANCE'];
/**
 * IECN per-mile schedule.
 * Source: "Pricing Schedule for Integrity Express Courier Network", contract-ready folder.
 *
 * The published schedule lists Standard scheduled and STAT only. RUSH is DERIVED as the
 * midpoint of the two — it is not a published rate. Confirm before selling it.
 */
export const IECN_PER_MILE = {
    enabled: true,
    bands: [
        {
            key: 'LOCAL',
            label: 'Local',
            fromMiles: 0,
            toMiles: 20,
            bandLabel: '0–20 mi',
            timeframe: {
                ROUTINE: 'Same day · 1–2 hrs',
                RUSH: 'Same day · 1–2 hrs',
                STAT: 'STAT · 1–2 hrs',
            },
            ratePerMileCents: { ROUTINE: 525, RUSH: 575, STAT: 625 },
        },
        {
            key: 'REGIONAL',
            label: 'Regional',
            fromMiles: 20,
            toMiles: 51,
            bandLabel: '21–51 mi',
            timeframe: {
                ROUTINE: 'Same day · 1–2 hrs',
                RUSH: 'Same day · 1–2 hrs',
                STAT: 'STAT · 1–2 hrs',
            },
            ratePerMileCents: { ROUTINE: 450, RUSH: 488, STAT: 525 },
        },
        {
            key: 'MID_RANGE',
            label: 'Mid-Range',
            fromMiles: 51,
            toMiles: 72,
            bandLabel: '52–72 mi',
            timeframe: {
                ROUTINE: 'Same day · 1–3 hrs',
                RUSH: 'Same day · 1–3 hrs',
                STAT: 'STAT · 1–3 hrs',
            },
            ratePerMileCents: { ROUTINE: 400, RUSH: 448, STAT: 495 },
        },
        {
            key: 'LONG_DISTANCE',
            label: 'Long-Distance',
            fromMiles: 72,
            toMiles: null,
            bandLabel: '73+ mi',
            timeframe: {
                ROUTINE: 'Same day · 1–4 hrs',
                RUSH: 'Same day · 1–4 hrs',
                STAT: 'STAT · 1–4 hrs',
            },
            ratePerMileCents: { ROUTINE: 395, RUSH: 430, STAT: 465 },
        },
    ],
    extendedDurationMinutes: 240,
    extendedSurchargePerMileCents: 25,
    overnightPerMileCents: 100,
    minimumChargeCents: { ROUTINE: 2500, RUSH: 3000, STAT: 3500 },
    /**
     * Non-sensitive (STANDARD segment) LOCAL rate — roughly half the published MEDICAL rate
     * above, competitive against typical $1.50-3/mi courier market pricing for regular
     * freight. Confirmed by IECN specifically for the LOCAL band; Regional and beyond stay
     * the same for every customer.
     */
    standardLocalRatePerMileCents: { ROUTINE: 265, RUSH: 365, STAT: 500 },
};
export function mileageBandFor(miles, rules = IECN_PER_MILE) {
    if (!Number.isFinite(miles) || miles < 0) {
        throw new RangeError(`invalid distance: ${String(miles)}`);
    }
    for (const band of rules.bands) {
        const aboveFloor = band.fromMiles === 0 ? miles >= 0 : miles > band.fromMiles;
        const belowCeiling = band.toMiles === null || miles <= band.toMiles;
        if (aboveFloor && belowCeiling)
            return band;
    }
    const last = rules.bands[rules.bands.length - 1];
    if (!last)
        throw new RangeError('no mileage bands are configured');
    return last;
}
export const IECN_BUNDLE_PRICING = {
    enabled: true,
    tiers: [
        { minStops: 1, maxStops: 1, flatCents: 2000, perAdditionalStopCents: 0, label: 'Single stop' },
        { minStops: 2, maxStops: 6, flatCents: 3000, perAdditionalStopCents: 1400, label: 'Small batch' },
        { minStops: 7, maxStops: null, flatCents: 10000, perAdditionalStopCents: 1400, label: 'Route batch' },
    ],
    includedMilesPerStop: 10,
    perMileOverCents: 175,
    allowedServiceLevels: ['ROUTINE', 'RUSH'],
    /**
     * Discounted ladder for the STANDARD segment. IECN set the 1-stop and 2-stop anchors
     * directly ($15 / $20); the 7-stop base is derived rather than stated, keeping the same
     * $14 marginal per-stop rate as `tiers` above so 6→7 stops does not jump inconsistently:
     * $20 + 4 × $14 = $76 at 6 stops, so 7 stops = $76 + $14 = $90. Revisit if IECN wants a
     * different route-batch anchor.
     */
    standardTiers: [
        { minStops: 1, maxStops: 1, flatCents: 1500, perAdditionalStopCents: 0, label: 'Single stop' },
        { minStops: 2, maxStops: 6, flatCents: 2000, perAdditionalStopCents: 1400, label: 'Small batch' },
        { minStops: 7, maxStops: null, flatCents: 9000, perAdditionalStopCents: 1400, label: 'Route batch' },
    ],
};
export function tierForStops(stops, rules) {
    if (!Number.isInteger(stops) || stops < 1) {
        throw new RangeError(`stop count must be a positive integer, received ${String(stops)}`);
    }
    const sorted = [...rules.tiers].sort((a, b) => a.minStops - b.minStops);
    let match;
    for (const tier of sorted) {
        if (stops >= tier.minStops && (tier.maxStops === null || stops <= tier.maxStops)) {
            match = tier;
        }
    }
    if (!match) {
        const top = sorted[sorted.length - 1];
        if (!top)
            throw new RangeError('no volume tiers are configured');
        match = top;
    }
    return match;
}
/**
 * Price a batch. Surcharges behave exactly as they do on a single delivery so a
 * weekend route or a cold-chain run is never accidentally sold at the weekday rate.
 */
export function calculateBundlePrice(request, rules = IECN_RATE_CARD) {
    const { stops, totalMiles, serviceLevel, pricingSegment = 'MEDICAL', temperatureControlled = false, scheduledFor = new Date(), holidays = [], waitMinutes = 0, discountPercent = 0, discountCents = 0, overrideTotalCents, } = request;
    const bundle = rules.bundlePricing;
    if (!bundle.enabled) {
        throw new Error('Volume pricing is disabled. Enable it in Settings before quoting a batch.');
    }
    if (!bundle.allowedServiceLevels.includes(serviceLevel)) {
        throw new RangeError(`${serviceLevel} is not available as a batch. Allowed: ${bundle.allowedServiceLevels.join(', ')}.`);
    }
    if (!Number.isFinite(totalMiles) || totalMiles < 0) {
        throw new RangeError(`invalid batch distance: ${String(totalMiles)}`);
    }
    // STANDARD batches use the discounted ladder when one is configured; MEDICAL always
    // uses the published tiers, and so does STANDARD when no discount has been set.
    const effectiveTiers = pricingSegment === 'STANDARD' && bundle.standardTiers ? bundle.standardTiers : bundle.tiers;
    const tier = tierForStops(stops, { ...bundle, tiers: effectiveTiers });
    const lines = [];
    // 1. Tier flat rate, plus any stops above the tier's entry count.
    const extraStops = Math.max(0, stops - tier.minStops);
    const stopsCents = extraStops * tier.perAdditionalStopCents;
    lines.push({
        code: 'BUNDLE_BASE',
        label: `${tier.label} — ${tier.minStops} stop${tier.minStops === 1 ? '' : 's'}`,
        amountCents: tier.flatCents,
    });
    if (stopsCents > 0) {
        lines.push({
            code: 'BUNDLE_STOPS',
            label: `Additional stops (${extraStops})`,
            amountCents: stopsCents,
        });
    }
    // 2. Mileage beyond the batch allowance.
    const includedMiles = bundle.includedMilesPerStop * stops;
    let mileageCents = 0;
    if (totalMiles > includedMiles) {
        const overMiles = Math.ceil(totalMiles - includedMiles);
        mileageCents = overMiles * bundle.perMileOverCents;
        lines.push({
            code: 'BUNDLE_MILEAGE',
            label: `Mileage beyond ${includedMiles} included mi (${overMiles} mi)`,
            amountCents: mileageCents,
        });
    }
    // 3. Weekend / holiday multiplies the line haul, as on the rate card.
    const weekendOrHoliday = isWeekendOrHoliday(scheduledFor, holidays, rules);
    let lineHaulCents = addCents(tier.flatCents, stopsCents, mileageCents);
    if (weekendOrHoliday && rules.surcharges.weekendHolidayMultiplier !== 1) {
        const uplifted = multiplyCents(lineHaulCents, rules.surcharges.weekendHolidayMultiplier);
        lines.push({
            code: 'WEEKEND_HOLIDAY',
            label: `Weekend / holiday (${rules.surcharges.weekendHolidayMultiplier}× standard rate)`,
            amountCents: uplifted - lineHaulCents,
        });
        lineHaulCents = uplifted;
    }
    // 4. Flat add-ons.
    let addOnCents = 0;
    if (temperatureControlled) {
        lines.push({
            code: 'TEMP_CONTROLLED',
            label: 'Temperature-controlled (cold-chain / specimen)',
            amountCents: rules.surcharges.temperatureControlledCents,
        });
        addOnCents = addCents(addOnCents, rules.surcharges.temperatureControlledCents);
    }
    const afterHours = isAfterHours(scheduledFor, rules);
    if (afterHours) {
        lines.push({
            code: 'AFTER_HOURS',
            label: 'After-hours / overnight',
            amountCents: rules.surcharges.afterHoursCents,
        });
        addOnCents = addCents(addOnCents, rules.surcharges.afterHoursCents);
    }
    const waitCents = waitTimeCharge(waitMinutes, rules);
    if (waitCents > 0) {
        const billable = Math.floor(waitMinutes - rules.waitTime.freeMinutes);
        lines.push({
            code: 'WAIT_TIME',
            label: `Wait time (${billable} min beyond ${rules.waitTime.freeMinutes} min included)`,
            amountCents: waitCents,
        });
        addOnCents = addCents(addOnCents, waitCents);
    }
    let subtotalCents = addCents(lineHaulCents, addOnCents);
    if (rules.surcharges.fuelSurchargePercent > 0) {
        const fuel = percentOf(subtotalCents, rules.surcharges.fuelSurchargePercent);
        lines.push({
            code: 'FUEL',
            label: `Fuel surcharge (${rules.surcharges.fuelSurchargePercent}%)`,
            amountCents: fuel,
        });
        subtotalCents = addCents(subtotalCents, fuel);
    }
    // 5. Discounts.
    let totalDiscount = 0;
    if (discountPercent > 0)
        totalDiscount = addCents(totalDiscount, percentOf(subtotalCents, discountPercent));
    if (discountCents > 0)
        totalDiscount = addCents(totalDiscount, discountCents);
    if (totalDiscount > subtotalCents)
        totalDiscount = subtotalCents;
    if (totalDiscount > 0) {
        lines.push({ code: 'DISCOUNT', label: 'Volume discount', amountCents: -totalDiscount });
    }
    let totalCents = subtotalCents - totalDiscount;
    let overrideApplied = false;
    if (typeof overrideTotalCents === 'number') {
        assertCents(overrideTotalCents, 'overrideTotalCents');
        lines.push({
            code: 'OVERRIDE',
            label: 'Contract price override',
            amountCents: overrideTotalCents - totalCents,
            note: 'Applied by authorised staff — recorded in the audit log.',
        });
        totalCents = overrideTotalCents;
        overrideApplied = true;
    }
    // A batch is priced off the in-city volume ladder, which is a flat-rate construct.
    const internal = calculateInternalEconomics({
        totalCents,
        lineHaulCents,
        waitCents,
        additionalStops: 0,
        pricingMode: 'FLAT_RATE',
        rules,
    });
    return {
        mode: 'BUNDLE',
        // A batch is quoted off the in-city volume ladder, which is a flat-rate construct.
        pricingMode: 'FLAT_RATE',
        pricingSegment,
        currency: rules.currency,
        rulesVersion: rules.version,
        stops,
        totalMiles: Math.round(totalMiles * 100) / 100,
        includedMiles,
        tierLabel: tier.label,
        serviceLevel,
        lines,
        lineHaulCents,
        subtotalCents,
        discountCents: totalDiscount,
        totalCents,
        perStopCents: Math.round(totalCents / stops),
        internal,
        flags: {
            afterHours,
            weekendOrHoliday,
            overrideApplied,
            belowMinimumCharge: false,
        },
    };
}
/**
 * What a batch would have cost as N separate rate-card deliveries, so a route customer
 * can be shown the saving and dispatch can see what the discount is costing IECN.
 */
export function bundleSavings(input) {
    const { stops, milesPerStop, serviceLevel, bundleTotalCents, scheduledFor, rules = IECN_RATE_CARD } = input;
    const single = calculatePrice({ miles: milesPerStop, serviceLevel, scheduledFor }, rules);
    const rateCardTotalCents = single.totalCents * stops;
    const savingCents = rateCardTotalCents - bundleTotalCents;
    return {
        rateCardTotalCents,
        savingCents,
        savingPercent: rateCardTotalCents === 0 ? 0 : Math.round((savingCents / rateCardTotalCents) * 1000) / 10,
    };
}
/**
 * IECN published rate card, effective 2026-06-04.
 * Source: Docs/06-04-2026-IECN-Pricing-Schedule.pdf
 */
export const IECN_RATE_CARD = {
    version: '2026.06.04',
    effectiveFrom: '2026-06-04',
    currency: 'USD',
    source: 'IECN Pricing Schedule 06-04-2026',
    matrixCents: {
        // In-city flat rate, set by IECN: $20 for a single in-city delivery. Two or more
        // in-city stops are priced on the volume ladder in `bundlePricing` ($30 for 2 … $100
        // for 7). RUSH and STAT are DERIVED from the Local band's published ratios
        // (RUSH 1.5×, STAT 2.5× of ROUTINE) — confirm in Settings before selling them.
        CITY: { ROUTINE: 2000, RUSH: 3000, STAT: 5000 },
        LOCAL: { ROUTINE: 3200, RUSH: 4800, STAT: 8000 },
        REGIONAL: { ROUTINE: 4500, RUSH: 6200, STAT: 10500 },
        EXTENDED: { ROUTINE: 6500, RUSH: 8500, STAT: 14500 },
        LONG_DISTANCE: { ROUTINE: 6500, RUSH: 8500, STAT: 14500 },
    },
    // The flat rate covers the first 10 miles; beyond that it is $2.25/mi at every service
    // level. Set by IECN, and deliberately uniform — one number to quote on the phone.
    overagePerMileCents: { ROUTINE: 225, RUSH: 225, STAT: 225 },
    overageAfterMiles: 10,
    surcharges: {
        afterHoursCents: 2500,
        weekendHolidayMultiplier: 1.5,
        temperatureControlledCents: 2000,
        fuelSurchargePercent: 0,
        additionalStopCents: 0,
        vehicleSurchargeCents: {
            CAR: 0,
            SUV: 0,
            PICKUP_TRUCK: 0,
            CARGO_VAN: 0,
            SPRINTER_VAN: 0,
            BOX_TRUCK: 0,
        },
    },
    waitTime: { freeMinutes: 10, perMinuteCents: 150 },
    businessHours: {
        timezone: 'America/New_York',
        weekdays: [1, 2, 3, 4, 5],
        startMinuteOfDay: 8 * 60,
        endMinuteOfDay: 18 * 60,
    },
    minimumChargeCents: {
        CITY: 2000,
        LOCAL: 3200,
        REGIONAL: 4500,
        EXTENDED: 6500,
        LONG_DISTANCE: 6500,
    },
    driverPay: {
        // Flat trip fee — ASSUMPTION, dollar figure not yet confirmed by IECN. $10 is a
        // starting point: enough to cover fuel/opportunity cost of accepting any job at all.
        baseCents: 1000,
        // Confirmed by IECN: 55%, down from an initial 65% placeholder. Because this applies
        // only to the line-haul portion (base fee stays flat), IECN's share of the price grows
        // as distance grows — the flat fee is a shrinking slice of a longer, pricier job.
        lineHaulSharePercent: 55,
        waitTimeSharePercent: 100,
        perStopCents: 0,
        minimumPerDeliveryCents: 0,
        // The percentage is confirmed; the flat base-fee figure above is still not. Flip this
        // once IECN has set both.
        confirmedByOperator: false,
    },
    processorFeePercent: 2.9,
    processorFeeFixedCents: 30,
    // Lowered from 35% to reflect what a 55%-share + flat-fee courier split actually produces
    // across the published rate card (roughly 38–45%, see docs/PRICING_ENGINE.md for the
    // worked table). 35% was flagging almost every job as low-margin, which is noise, not
    // signal. This threshold should still catch a genuinely bad job — a heavy discount, an
    // under-priced override, or a long per-mile run that undercuts the flat rate too far.
    minimumMarginPercent: 25,
    quoteValidMinutes: 15,
    bundlePricing: IECN_BUNDLE_PRICING,
    perMile: IECN_PER_MILE,
};
// ---------------------------------------------------------------------------
// Zone resolution
// ---------------------------------------------------------------------------
export function zoneForMiles(miles) {
    if (!Number.isFinite(miles) || miles < 0) {
        throw new RangeError(`invalid distance: ${String(miles)}`);
    }
    if (miles <= ZONE_SPECS.LOCAL.toMiles)
        return 'LOCAL';
    if (miles <= ZONE_SPECS.REGIONAL.toMiles)
        return 'REGIONAL';
    if (miles <= ZONE_SPECS.EXTENDED.toMiles)
        return 'EXTENDED';
    return 'LONG_DISTANCE';
}
/**
 * Resolve the billing zone.
 *
 * `inCityLimits` is true only when BOTH ends of the run sit inside a service area tagged
 * as city limits — the API computes it from the geocoded coordinates, never from what a
 * customer types. A run that starts in town and ends outside it is priced on distance.
 */
export function resolveZone(input) {
    if (input.inCityLimits) {
        if (!Number.isFinite(input.miles) || input.miles < 0) {
            throw new RangeError(`invalid distance: ${String(input.miles)}`);
        }
        return 'CITY';
    }
    return zoneForMiles(input.miles);
}
/** Minute-of-day and weekday for a timestamp in a given IANA timezone. */
function zonedParts(date, timezone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour12: false,
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
    const parts = formatter.formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const hour = Number(get('hour')) % 24;
    const minute = Number(get('minute'));
    return {
        weekday: weekdayMap[get('weekday')] ?? 0,
        minuteOfDay: hour * 60 + minute,
        ymd: `${get('year')}-${get('month')}-${get('day')}`,
    };
}
export function isAfterHours(at, rules = IECN_RATE_CARD) {
    const { weekday, minuteOfDay } = zonedParts(at, rules.businessHours.timezone);
    if (!rules.businessHours.weekdays.includes(weekday))
        return false; // weekend priced by multiplier
    return (minuteOfDay < rules.businessHours.startMinuteOfDay ||
        minuteOfDay >= rules.businessHours.endMinuteOfDay);
}
export function isWeekendOrHoliday(at, holidays = [], rules = IECN_RATE_CARD) {
    const { weekday, ymd } = zonedParts(at, rules.businessHours.timezone);
    if (!rules.businessHours.weekdays.includes(weekday))
        return true;
    return holidays.includes(ymd);
}
/** Billable wait charge given total minutes on site. */
export function waitTimeCharge(totalMinutes, rules = IECN_RATE_CARD) {
    if (!Number.isFinite(totalMinutes) || totalMinutes <= rules.waitTime.freeMinutes)
        return 0;
    const billable = Math.floor(totalMinutes - rules.waitTime.freeMinutes);
    return billable * rules.waitTime.perMinuteCents;
}
export function calculatePrice(request, rules = IECN_RATE_CARD) {
    const { serviceLevel, miles, inCityLimits = false, pricingMode = 'FLAT_RATE', pricingSegment = 'MEDICAL', durationMinutes, overnight = false, vehicleClass = 'CAR', additionalStops = 0, temperatureControlled = false, scheduledFor = new Date(), holidays = [], waitMinutes = 0, discountPercent = 0, discountCents = 0, overrideTotalCents, } = request;
    if (!SERVICE_LEVELS.includes(serviceLevel)) {
        throw new RangeError(`unknown service level: ${String(serviceLevel)}`);
    }
    const zone = resolveZone({ miles, inCityLimits });
    const lines = [];
    let baseCents = 0;
    let overageCents = 0;
    let mileageBandLabel;
    /**
     * The flat rate's base — and therefore its floor — is the entry band. Mileage past the
     * included allowance is charged explicitly, so the wider Regional/Extended band bases
     * would charge for distance twice.
     */
    const flatBaseZone = zone === 'CITY' ? 'CITY' : 'LOCAL';
    if (pricingMode === 'PER_MILE') {
        if (!rules.perMile.enabled) {
            throw new Error('Per-mile pricing is disabled. Enable it in Settings before quoting it.');
        }
        // Billed miles are rounded up — a partial mile is still a mile driven.
        const billableMiles = Math.max(1, Math.ceil(miles));
        const band = mileageBandFor(miles, rules.perMile);
        mileageBandLabel = `${band.label} (${band.bandLabel})`;
        // Only the LOCAL band is ever split by segment — Regional and beyond bill everyone
        // the published rate regardless of what the STANDARD discount does to Local.
        const standardRate = band.key === 'LOCAL' ? rules.perMile.standardLocalRatePerMileCents?.[serviceLevel] : undefined;
        const rate = pricingSegment === 'STANDARD' && standardRate !== undefined
            ? standardRate
            : band.ratePerMileCents[serviceLevel];
        baseCents = billableMiles * rate;
        lines.push({
            code: 'PER_MILE',
            label: `${band.label} ${SERVICE_LEVEL_SPECS[serviceLevel].label} — ${billableMiles} mi @ ${(rate / 100).toFixed(2)}/mi`,
            amountCents: baseCents,
        });
        if (overnight) {
            const overnightCents = billableMiles * rules.perMile.overnightPerMileCents;
            lines.push({
                code: 'OVERNIGHT',
                label: `Overnight (${billableMiles} mi @ ${(rules.perMile.overnightPerMileCents / 100).toFixed(2)}/mi)`,
                amountCents: overnightCents,
            });
            overageCents = addCents(overageCents, overnightCents);
        }
        // Long-distance work running beyond the threshold carries the published per-mile uplift.
        if (band.key === 'LONG_DISTANCE' &&
            typeof durationMinutes === 'number' &&
            durationMinutes > rules.perMile.extendedDurationMinutes) {
            const extendedCents = billableMiles * rules.perMile.extendedSurchargePerMileCents;
            lines.push({
                code: 'EXTENDED_DURATION',
                label: `Beyond ${Math.round(rules.perMile.extendedDurationMinutes / 60)} hrs (${billableMiles} mi @ ${(rules.perMile.extendedSurchargePerMileCents / 100).toFixed(2)}/mi)`,
                amountCents: extendedCents,
            });
            overageCents = addCents(overageCents, extendedCents);
        }
    }
    else {
        /**
         * FLAT RATE: one base covering the first `overageAfterMiles` miles, then a uniform
         * per-mile rate beyond it.
         *
         * The base is the entry band (CITY when the run is inside city limits, otherwise LOCAL)
         * because mileage past the included allowance is now charged explicitly. Using the
         * Regional/Extended band bases here as well would charge for distance twice — those
         * bands remain in the rate card for reference and for the published schedule, but flat
         * mode does not read them.
         */
        baseCents = rules.matrixCents[flatBaseZone][serviceLevel];
        lines.push({
            code: 'FLAT_BASE',
            label: `${ZONE_SPECS[flatBaseZone].label} ${SERVICE_LEVEL_SPECS[serviceLevel].label} flat rate (first ${rules.overageAfterMiles} mi included)`,
            amountCents: baseCents,
        });
        if (miles > rules.overageAfterMiles) {
            const overMiles = Math.ceil(miles - rules.overageAfterMiles);
            overageCents = overMiles * rules.overagePerMileCents[serviceLevel];
            lines.push({
                code: 'MILEAGE',
                label: `Mileage beyond ${rules.overageAfterMiles} mi (${overMiles} mi @ ${(rules.overagePerMileCents[serviceLevel] / 100).toFixed(2)}/mi)`,
                amountCents: overageCents,
            });
        }
    }
    // 3. Weekend / holiday multiplies the standard rate (base + mileage).
    const weekendOrHoliday = isWeekendOrHoliday(scheduledFor, holidays, rules);
    let lineHaulCents = addCents(baseCents, overageCents);
    if (weekendOrHoliday && rules.surcharges.weekendHolidayMultiplier !== 1) {
        const uplifted = multiplyCents(lineHaulCents, rules.surcharges.weekendHolidayMultiplier);
        lines.push({
            code: 'WEEKEND_HOLIDAY',
            label: `Weekend / holiday (${rules.surcharges.weekendHolidayMultiplier}× standard rate)`,
            amountCents: uplifted - lineHaulCents,
        });
        lineHaulCents = uplifted;
    }
    // 3b. Minimum charge floors the core delivery service itself, before any add-on.
    // Flooring the grand TOTAL instead (as this used to) let an unrelated surcharge — an
    // after-hours fee, cold-chain handling — silently satisfy the floor while the actual
    // per-mile service underneath stayed priced at pennies. An after-hours fee pays for
    // the after-hours effort; it was never meant to subsidize an underpriced delivery.
    const zoneMinimum = pricingMode === 'PER_MILE'
        ? rules.perMile.minimumChargeCents[serviceLevel]
        : rules.minimumChargeCents[flatBaseZone];
    let belowMinimum = lineHaulCents < zoneMinimum;
    if (belowMinimum) {
        lines.push({
            code: 'MINIMUM_CHARGE',
            label: 'Minimum delivery charge adjustment',
            amountCents: zoneMinimum - lineHaulCents,
        });
        lineHaulCents = zoneMinimum;
    }
    // 4. Flat add-ons.
    let addOnCents = 0;
    if (additionalStops > 0 && rules.surcharges.additionalStopCents > 0) {
        const stops = additionalStops * rules.surcharges.additionalStopCents;
        lines.push({
            code: 'ADDITIONAL_STOPS',
            label: `Additional stops (${additionalStops})`,
            amountCents: stops,
        });
        addOnCents = addCents(addOnCents, stops);
    }
    if (temperatureControlled) {
        lines.push({
            code: 'TEMP_CONTROLLED',
            label: 'Temperature-controlled (cold-chain / specimen)',
            amountCents: rules.surcharges.temperatureControlledCents,
        });
        addOnCents = addCents(addOnCents, rules.surcharges.temperatureControlledCents);
    }
    const afterHours = isAfterHours(scheduledFor, rules);
    if (afterHours) {
        lines.push({
            code: 'AFTER_HOURS',
            label: 'After-hours / overnight',
            amountCents: rules.surcharges.afterHoursCents,
        });
        addOnCents = addCents(addOnCents, rules.surcharges.afterHoursCents);
    }
    const vehicleSurcharge = rules.surcharges.vehicleSurchargeCents[vehicleClass] ?? 0;
    if (vehicleSurcharge > 0) {
        lines.push({ code: 'VEHICLE', label: `Vehicle: ${vehicleClass}`, amountCents: vehicleSurcharge });
        addOnCents = addCents(addOnCents, vehicleSurcharge);
    }
    const waitCents = waitTimeCharge(waitMinutes, rules);
    if (waitCents > 0) {
        const billable = Math.floor(waitMinutes - rules.waitTime.freeMinutes);
        lines.push({
            code: 'WAIT_TIME',
            label: `Wait time (${billable} min beyond ${rules.waitTime.freeMinutes} min included)`,
            amountCents: waitCents,
        });
        addOnCents = addCents(addOnCents, waitCents);
    }
    // 5. Fuel surcharge on the subtotal, per current market index.
    let subtotalCents = addCents(lineHaulCents, addOnCents);
    if (rules.surcharges.fuelSurchargePercent > 0) {
        const fuel = percentOf(subtotalCents, rules.surcharges.fuelSurchargePercent);
        lines.push({
            code: 'FUEL',
            label: `Fuel surcharge (${rules.surcharges.fuelSurchargePercent}%)`,
            amountCents: fuel,
        });
        subtotalCents = addCents(subtotalCents, fuel);
    }
    // 6. Discounts.
    let totalDiscount = 0;
    if (discountPercent > 0) {
        totalDiscount = addCents(totalDiscount, percentOf(subtotalCents, discountPercent));
    }
    if (discountCents > 0) {
        assertCents(discountCents, 'discountCents');
        totalDiscount = addCents(totalDiscount, discountCents);
    }
    if (totalDiscount > subtotalCents)
        totalDiscount = subtotalCents;
    if (totalDiscount > 0) {
        lines.push({ code: 'DISCOUNT', label: 'Discount', amountCents: -totalDiscount });
    }
    // 7. Total. The line haul was already floored in step 3b, protecting it from surcharges
    // masking an underpriced core service. This second check is narrower: it only ever
    // fires when a discount drags an already-correctly-priced job back below the same
    // floor — a discount must never buy a job below its zone minimum either.
    let totalCents = subtotalCents - totalDiscount;
    if (totalCents < zoneMinimum) {
        lines.push({
            code: 'MINIMUM_CHARGE',
            label: 'Minimum delivery charge adjustment',
            amountCents: zoneMinimum - totalCents,
        });
        totalCents = zoneMinimum;
        belowMinimum = true;
    }
    let overrideApplied = false;
    if (typeof overrideTotalCents === 'number') {
        assertCents(overrideTotalCents, 'overrideTotalCents');
        lines.push({
            code: 'OVERRIDE',
            label: 'Manual price override',
            amountCents: overrideTotalCents - totalCents,
            note: 'Applied by authorised staff — recorded in the audit log.',
        });
        totalCents = overrideTotalCents;
        overrideApplied = true;
    }
    // 8. Internal economics.
    const internal = calculateInternalEconomics({
        totalCents,
        lineHaulCents,
        waitCents,
        additionalStops,
        pricingMode,
        rules,
    });
    return {
        currency: rules.currency,
        rulesVersion: rules.version,
        pricingMode,
        pricingSegment,
        zone,
        zoneLabel: mileageBandLabel ?? ZONE_SPECS[zone].label,
        miles: Math.round(miles * 100) / 100,
        serviceLevel,
        lines,
        lineHaulCents,
        subtotalCents,
        discountCents: totalDiscount,
        totalCents,
        internal,
        flags: {
            afterHours,
            weekendOrHoliday,
            overrideApplied,
            belowMinimumCharge: belowMinimum,
        },
    };
}
function calculateInternalEconomics(input) {
    const { totalCents, lineHaulCents, waitCents, additionalStops, pricingMode, rules } = input;
    const pay = rules.driverPay;
    // The flat trip fee only applies to flat-rate jobs. A per-mile price is already scaled
    // down for short runs, so stacking a flat fee on top of it disproportionately eats a
    // cheap job's margin — exactly the jobs per-mile exists to win. See docs/PRICING_ENGINE.md.
    const tripFee = pricingMode === 'FLAT_RATE' ? pay.baseCents : 0;
    const driverPay = clampMin(addCents(tripFee, percentOf(lineHaulCents, pay.lineHaulSharePercent), percentOf(waitCents, pay.waitTimeSharePercent), additionalStops * pay.perStopCents), pay.minimumPerDeliveryCents);
    const processorFee = addCents(percentOf(totalCents, rules.processorFeePercent), rules.processorFeeFixedCents);
    const variableCost = addCents(driverPay, processorFee);
    const grossProfit = totalCents - variableCost;
    const margin = marginPercent(totalCents, variableCost);
    return {
        estimatedDriverPayCents: driverPay,
        estimatedProcessorFeeCents: processorFee,
        estimatedVariableCostCents: variableCost,
        estimatedGrossProfitCents: grossProfit,
        estimatedGrossMarginPercent: margin,
        lowMargin: margin < rules.minimumMarginPercent,
        driverPayRulesConfirmed: pay.confirmedByOperator,
    };
}
/**
 * Quote a job BOTH ways so the customer can choose.
 *
 * The two schedules cross over around 6–7 miles. Presenting both and leading with the
 * cheaper one is a competitive posture: IECN wins the job but receives the lower of the two
 * prices. Staff see the margin on each in the internal block, so a dispatcher can tell at a
 * glance when the cheaper option is not worth taking.
 */
export function quoteBothWays(request, rules = IECN_RATE_CARD) {
    const flat = calculatePrice({ ...request, pricingMode: 'FLAT_RATE' }, rules);
    const perMile = calculatePrice({ ...request, pricingMode: 'PER_MILE' }, rules);
    const flatCheaper = flat.totalCents <= perMile.totalCents;
    const difference = Math.abs(flat.totalCents - perMile.totalCents);
    const options = [
        {
            mode: 'FLAT_RATE',
            label: 'Flat rate',
            description: `One price covering the first ${rules.overageAfterMiles} miles, then ${(rules.overagePerMileCents[request.serviceLevel] / 100).toFixed(2)} per mile.`,
            totalCents: flat.totalCents,
            breakdown: flat,
            cheaper: flatCheaper,
            differenceCents: flatCheaper ? -difference : difference,
        },
        {
            mode: 'PER_MILE',
            label: 'Per mile',
            description: `Billed by the mile at the ${mileageBandFor(request.miles, rules.perMile).label} rate.`,
            totalCents: perMile.totalCents,
            breakdown: perMile,
            cheaper: !flatCheaper,
            differenceCents: flatCheaper ? difference : -difference,
        },
    ];
    return {
        options,
        recommended: flatCheaper ? 'FLAT_RATE' : 'PER_MILE',
        savingCents: difference,
    };
}
/** Strip every internal line and figure before a breakdown is sent to a customer. */
export function customerSafeBreakdown(breakdown) {
    return {
        currency: breakdown.currency,
        zoneLabel: breakdown.zoneLabel,
        miles: breakdown.miles,
        serviceLevel: breakdown.serviceLevel,
        lines: breakdown.lines
            .filter((line) => !line.internal && line.code !== 'OVERRIDE')
            .map(({ code, label, amountCents }) => ({ code, label, amountCents })),
        subtotalCents: breakdown.subtotalCents,
        discountCents: breakdown.discountCents,
        totalCents: breakdown.totalCents,
    };
}
/** Actual driver earning for a completed job. Mirrors the quote-time estimate. */
export function calculateDriverEarning(input) {
    const { lineHaulCents, billedWaitCents = 0, additionalStops = 0, bonusCents = 0, adjustmentCents = 0, pricingMode = 'FLAT_RATE', rules = IECN_RATE_CARD, } = input;
    const pay = rules.driverPay;
    const lines = [];
    let total = 0;
    if (pricingMode === 'FLAT_RATE' && pay.baseCents > 0) {
        lines.push({ code: 'BASE', label: 'Trip fee (flat)', amountCents: pay.baseCents });
        total = addCents(total, pay.baseCents);
    }
    const share = percentOf(lineHaulCents, pay.lineHaulSharePercent);
    lines.push({
        code: 'SHARE',
        label: `Delivery pay (${pay.lineHaulSharePercent}% of line haul)`,
        amountCents: share,
    });
    total = addCents(total, share);
    if (billedWaitCents > 0) {
        const wait = percentOf(billedWaitCents, pay.waitTimeSharePercent);
        lines.push({ code: 'WAIT', label: 'Wait time', amountCents: wait });
        total = addCents(total, wait);
    }
    if (additionalStops > 0 && pay.perStopCents > 0) {
        const stops = additionalStops * pay.perStopCents;
        lines.push({ code: 'STOPS', label: `Additional stops (${additionalStops})`, amountCents: stops });
        total = addCents(total, stops);
    }
    if (bonusCents !== 0) {
        lines.push({ code: 'BONUS', label: 'Bonus', amountCents: bonusCents });
        total = addCents(total, bonusCents);
    }
    if (adjustmentCents !== 0) {
        lines.push({ code: 'ADJUSTMENT', label: 'Adjustment', amountCents: adjustmentCents });
        total = addCents(total, adjustmentCents);
    }
    if (total < pay.minimumPerDeliveryCents) {
        lines.push({
            code: 'MINIMUM',
            label: 'Minimum delivery guarantee',
            amountCents: pay.minimumPerDeliveryCents - total,
        });
        total = pay.minimumPerDeliveryCents;
    }
    return { lines, totalCents: total };
}
export const VEHICLE_CAPABILITIES = {
    CAR: {
        key: 'CAR',
        label: 'Car',
        maxWeightLbs: 100,
        maxLongestSideInches: 40,
        description: 'Documents, specimens, small parcels.',
    },
    SUV: {
        key: 'SUV',
        label: 'SUV',
        maxWeightLbs: 300,
        maxLongestSideInches: 60,
        description: 'Several totes or bulky-but-light items.',
    },
    PICKUP_TRUCK: {
        key: 'PICKUP_TRUCK',
        label: 'Pickup truck',
        maxWeightLbs: 1500,
        maxLongestSideInches: 96,
        description: 'Open-bed loads, parts, equipment.',
    },
    CARGO_VAN: {
        key: 'CARGO_VAN',
        label: 'Cargo van',
        maxWeightLbs: 3000,
        maxLongestSideInches: 120,
        description: 'Enclosed, secure, multi-pallet-lite.',
    },
    SPRINTER_VAN: {
        key: 'SPRINTER_VAN',
        label: 'Sprinter van',
        maxWeightLbs: 4000,
        maxLongestSideInches: 144,
        description: 'Tall, high-volume enclosed freight.',
    },
    BOX_TRUCK: {
        key: 'BOX_TRUCK',
        label: 'Box truck',
        maxWeightLbs: 10000,
        maxLongestSideInches: 240,
        description: 'Palletised freight and liftgate work.',
    },
};
const VEHICLE_ORDER = [
    'CAR',
    'SUV',
    'PICKUP_TRUCK',
    'CARGO_VAN',
    'SPRINTER_VAN',
    'BOX_TRUCK',
];
export function recommendVehicle(input) {
    const { totalWeightLbs = 0, longestSideInches = 0, itemCount = 1 } = input;
    for (const key of VEHICLE_ORDER) {
        const spec = VEHICLE_CAPABILITIES[key];
        const weightOk = totalWeightLbs <= spec.maxWeightLbs;
        const sizeOk = longestSideInches <= spec.maxLongestSideInches;
        const countOk = key !== 'CAR' || itemCount <= 6;
        if (weightOk && sizeOk && countOk) {
            const reasons = [];
            if (totalWeightLbs > 0)
                reasons.push(`${totalWeightLbs} lb`);
            if (longestSideInches > 0)
                reasons.push(`${longestSideInches}" longest side`);
            if (itemCount > 1)
                reasons.push(`${itemCount} items`);
            return {
                recommended: key,
                reason: reasons.length
                    ? `${spec.label} handles ${reasons.join(', ')}.`
                    : `${spec.label} is sufficient for this shipment.`,
            };
        }
    }
    return {
        recommended: 'BOX_TRUCK',
        reason: 'Shipment exceeds van capacity — box truck required.',
    };
}
//# sourceMappingURL=pricing.js.map